import React, { useEffect, useState, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { friendMarker, meetMarker, destinationMarker, selfMarker, placeMarker } from '../lib/mapMarkers';
import { useSocket } from '../context/SocketContext';

const DEFAULT_CENTER = [59.33, 18.07];
const FOLLOW_ZOOM = 17;
const NAVIGATE_ZOOM = 18;

function RouteLayer({ route }) {
  const map = useMap();

  useEffect(() => {
    if (route && route.length > 0) {
      map.fitBounds(route);
    }
  }, [route, map]);

  if (!route || route.length === 0) return null;
  // Mork kantlinje under den limegrona gor rutten lasbar over alla kartrutor.
  return (
    <>
      <Polyline positions={route} color="#0c0d0e" weight={10} opacity={0.85} />
      <Polyline positions={route} color="#c8ff2e" weight={5} />
    </>
  );
}

function ClickHandler({ onMapClick, pickMode }) {
  useMapEvents({
    click(event) {
      if (pickMode && onMapClick) onMapClick([event.latlng.lat, event.latlng.lng]);
    },
  });
  return null;
}

/** Följer användaren så länge en rutt är igång. */
function FollowUser({ position, route, navigating }) {
  const map = useMap();
  const following = useRef(false);

  useEffect(() => {
    following.current = Boolean(route && route.length > 0);
    if (following.current && position) {
      map.setView(position, navigating ? NAVIGATE_ZOOM : FOLLOW_ZOOM);
    }
  }, [route, position, navigating, map]);

  useEffect(() => {
    if (following.current && position) map.setView(position, map.getZoom());
  }, [position, map]);

  return null;
}

export default function Map({
  route, onMapClick, pickMode, userPosition, navigating,
  meets = [], places = [], selectedPlaceId, onSelectPlace, onMapReady,
}) {
  const { socket, connected } = useSocket();
  const [peers, setPeers] = useState({});

  const position = userPosition || DEFAULT_CENTER;

  // Skicka egen position. Tidigare krävdes att socketen redan fanns när
  // positionen kom, vilket gjorde att den allra första GPS-fixen tappades.
  useEffect(() => {
    if (!socket || !connected || !userPosition) return;
    socket.emit('location:update', { lat: userPosition[0], lng: userPosition[1] });
  }, [socket, connected, userPosition]);

  useEffect(() => {
    if (!socket) return undefined;

    // Vänner som redan delar sin plats, så att kartan inte är tom tills
    // någon råkar röra på sig.
    const onSnapshot = (list) => {
      setPeers(Object.fromEntries(list.map(peer => [peer.userId, {
        lat: peer.lat,
        lng: peer.lng,
        username: peer.username,
        destination: peer.destination || null,
      }])));
    };

    const onPeer = (data) => {
      setPeers(prev => {
        if (data.lat === null || data.lat === undefined) {
          const next = { ...prev };
          delete next[data.userId];
          return next;
        }
        return {
          ...prev,
          [data.userId]: {
            ...prev[data.userId],
            lat: data.lat,
            lng: data.lng,
            username: data.username || `Vän #${data.userId}`,
            picture: data.picture !== undefined ? data.picture : (prev[data.userId] || {}).picture,
          },
        };
      });
    };

    const onDestination = (data) => {
      setPeers(prev => {
        const existing = prev[data.userId];
        if (!existing) return prev;
        const destination = (data.lat === null || data.lat === undefined)
          ? null
          : { lat: data.lat, lng: data.lng };
        return { ...prev, [data.userId]: { ...existing, destination } };
      });
    };

    socket.on('location:snapshot', onSnapshot);
    socket.on('location:peer', onPeer);
    socket.on('destination:peer', onDestination);

    return () => {
      socket.off('location:snapshot', onSnapshot);
      socket.off('location:peer', onPeer);
      socket.off('destination:peer', onDestination);
    };
  }, [socket]);

  const peerList = useMemo(() => Object.entries(peers), [peers]);
  // Markorerna byggs en gang och ateranvands mellan omritningar.
  const selfIcon = useMemo(() => selfMarker(), []);
  const meetIcon = useMemo(() => meetMarker(), []);
  const destinationIcon = useMemo(() => destinationMarker(), []);

  return (
    <div className="map-container">
      <MapContainer
        center={position}
        zoom={13}
        style={{ width: '100%', height: '100%' }}
        /* Egna, storre kontroller ersatter Leaflets sma standardknappar. */
        zoomControl={false}
        attributionControl={false}
        ref={onMapReady}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <Marker position={position} icon={selfIcon} zIndexOffset={500}>
          <Popup>Du är här</Popup>
        </Marker>

        {peerList.map(([id, peer]) => (
          <React.Fragment key={id}>
            <Marker
              position={[peer.lat, peer.lng]}
              icon={friendMarker({ username: peer.username, picture: peer.picture })}
            >
              <Tooltip permanent direction="top" className="map-marker__label">
                {peer.username}
              </Tooltip>
              <Popup>{peer.username}</Popup>
            </Marker>
            {peer.destination && (
              <Marker position={[peer.destination.lat, peer.destination.lng]} icon={destinationIcon}>
                <Popup>{peer.username} är på väg hit</Popup>
              </Marker>
            )}
          </React.Fragment>
        ))}

        {places.map(place => (
          <Marker
            key={place.id}
            position={[place.lat, place.lng]}
            icon={placeMarker(place.category, place.id === selectedPlaceId)}
            eventHandlers={{ click: () => onSelectPlace && onSelectPlace(place) }}
            zIndexOffset={place.id === selectedPlaceId ? 400 : 0}
          />
        ))}

        {meets.map(meet => (
          <Marker key={`meet-${meet.id}`} position={[meet.lat, meet.lng]} icon={meetIcon}>
            <Popup>
              <strong>{meet.title}</strong><br />
              {meet.description}<br />
              {new Date(meet.event_date).toLocaleString('sv-SE')}
            </Popup>
          </Marker>
        ))}

        <RouteLayer route={route} />
        <FollowUser position={userPosition} route={route} navigating={navigating} />
        <ClickHandler onMapClick={onMapClick} pickMode={pickMode} />
      </MapContainer>

      {pickMode && <div className="map-hint">Tryck på kartan för att välja plats</div>}
      <div className="map-credit">© OpenStreetMap</div>
    </div>
  );
}
